import { defineStore } from 'pinia'
import { ref, computed, watch } from 'vue'
import { useGeneratedAlerts } from '@/composables/useGeneratedAlerts'
import { useAuthStore } from '@/stores/auth'
import { useCompanyStore } from '@/stores/company'
import type { GeneratedAlert, AlertModule, AlertSeverity } from '@/types/alertRules'

export const useGeneratedAlertsStore = defineStore('generatedAlerts', () => {
  const alerts = ref<GeneratedAlert[]>([])
  const isLoading = ref(false)
  const error = ref<string | null>(null)
  const filterModule = ref<AlertModule | 'all'>('all')
  const filterSeverity = ref<AlertSeverity | 'all'>('all')
  const filterRead = ref<'all' | 'read' | 'unread'>('unread')

  let unsubscribe: (() => void) | null = null
  let subscriberCount = 0

  const authStore = useAuthStore()
  const companyStore = useCompanyStore()

  const {
    fetchAlerts,
    markAsRead,
    markAllAsRead,
    setupRealtimeListener
  } = useGeneratedAlerts()

  // ── Computed ──
  const filteredAlerts = computed(() => {
    let result = alerts.value

    if (filterModule.value !== 'all') {
      result = result.filter(a => a.module === filterModule.value)
    }
    if (filterSeverity.value !== 'all') {
      result = result.filter(a => a.severity === filterSeverity.value)
    }
    if (filterRead.value === 'read') {
      result = result.filter(a => a.read)
    } else if (filterRead.value === 'unread') {
      result = result.filter(a => !a.read)
    }

    return result
  })

  const unreadCount = computed(() => alerts.value.filter(a => !a.read).length)
  const totalCount = computed(() => alerts.value.length)

  const unreadByModule = computed(() => {
    const counts: Record<string, number> = {}
    alerts.value.filter(a => !a.read).forEach(a => {
      counts[a.module] = (counts[a.module] || 0) + 1
    })
    return counts
  })

  const unreadBySeverity = computed(() => {
    const counts: Record<string, number> = { info: 0, warning: 0, critical: 0 }
    alerts.value.filter(a => !a.read).forEach(a => {
      counts[a.severity] = (counts[a.severity] || 0) + 1
    })
    return counts
  })

  const totalBySeverity = computed(() => {
    const counts: Record<string, number> = { info: 0, warning: 0, critical: 0 }
    alerts.value.forEach(a => {
      counts[a.severity] = (counts[a.severity] || 0) + 1
    })
    return counts
  })

  const alertsByEntityId = computed(() => {
    const map: Record<string, GeneratedAlert[]> = {}
    for (const alert of alerts.value) {
      const entityId = alert.metadata?.entityId
      if (!entityId) continue
      if (!map[entityId]) map[entityId] = []
      map[entityId].push(alert)
    }
    return map
  })

  const unreadCountByEntityId = computed(() => {
    const map: Record<string, number> = {}
    for (const alert of alerts.value) {
      const entityId = alert.metadata?.entityId
      if (!entityId || alert.read) continue
      map[entityId] = (map[entityId] || 0) + 1
    }
    return map
  })

  // ── Acciones ──
  async function loadAlerts() {
    isLoading.value = true
    error.value = null
    try {
      alerts.value = await fetchAlerts()
    } catch (err) {
      error.value = err instanceof Error ? err.message : 'Error al cargar alertas'
    } finally {
      isLoading.value = false
    }
  }

  async function readAlert(alertId: string) {
    const alert = alerts.value.find(a => a.id === alertId)
    const visibility = alert?.visibility === 'company' ? 'company' : 'private'
    const success = await markAsRead(alertId, visibility as 'private' | 'company')
    if (success && alert) {
      alert.read = true
      alert.readAt = new Date()
    }
    return success
  }

  async function readAllAlerts() {
    const success = await markAllAsRead()
    if (success) {
      alerts.value.forEach(a => {
        if (!a.read) {
          a.read = true
          a.readAt = new Date()
        }
      })
    }
    return success
  }

  function setFilterModule(mod: AlertModule | 'all') {
    filterModule.value = mod
  }

  function setFilterSeverity(sev: AlertSeverity | 'all') {
    filterSeverity.value = sev
  }

  function setFilterRead(read: 'all' | 'read' | 'unread') {
    filterRead.value = read
  }

  function createListener() {
    const userId = authStore.user?.uid
    const companyId = companyStore.selectedCompanyId
    if (!userId || !companyId) return
    unsubscribe = setupRealtimeListener((updatedAlerts) => {
      alerts.value = updatedAlerts
    })
  }

  function destroyListener() {
    if (unsubscribe) {
      unsubscribe()
      unsubscribe = null
    }
  }

  function startRealtimeSync() {
    subscriberCount++
    if (unsubscribe) return
    createListener()
  }

  function stopRealtimeSync() {
    if (subscriberCount > 0) subscriberCount--
    if (subscriberCount === 0) {
      destroyListener()
    }
  }

  watch(
    () => [authStore.user?.uid, companyStore.selectedCompanyId] as const,
    ([uid, companyId]) => {
      if (subscriberCount === 0) return
      destroyListener()
      if (uid && companyId) {
        createListener()
      } else {
        alerts.value = []
      }
    }
  )

  function $reset() {
    destroyListener()
    subscriberCount = 0
    alerts.value = []
    isLoading.value = false
    error.value = null
    filterModule.value = 'all'
    filterSeverity.value = 'all'
    filterRead.value = 'all'
  }

  return {
    alerts,
    isLoading,
    error,
    filterModule,
    filterSeverity,
    filterRead,

    filteredAlerts,
    unreadCount,
    totalCount,
    unreadByModule,
    unreadBySeverity,
    totalBySeverity,
    alertsByEntityId,
    unreadCountByEntityId,

    loadAlerts,
    readAlert,
    readAllAlerts,
    setFilterModule,
    setFilterSeverity,
    setFilterRead,
    startRealtimeSync,
    stopRealtimeSync,
    $reset
  }
})
